/**
 * schema-form.tsx — a generic JSON-Schema → React form. Drives the command palette
 * and every entity action. Field lists are read from the schema arktype emits, so
 * adding a contract field never touches this file.
 */
import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { JsonSchema } from "./api.ts";
import { AutoTextarea } from "./auto-textarea.tsx";
import { refKindForField, type ResolvedRef } from "../../domain/resolve.ts";
import { keepInView } from "./scroll.ts";

/**
 * Supplies the form with the entity options behind a reference field, so a raw-id
 * input can become a searchable name-picker. `null` (the default) means "no
 * introspection available" — fields then fall back to plain text, so the form still
 * works outside the palette or on an empty workspace.
 */
type RefLookup = (kind: string) => ResolvedRef[];
const RefLookupContext = createContext<RefLookup | null>(null);

function schemaType(s: JsonSchema): string {
  if (s.const !== undefined) return "const";
  if (s.enum) return "enum";
  if (Array.isArray(s.type)) return s.type.find((t) => t !== "null") ?? "string";
  if (typeof s.type === "string") return s.type;
  if (s.properties) return "object";
  if (s.items) return "array";
  if (s.anyOf) return "union";
  return "string";
}

function defaultFor(s: JsonSchema): unknown {
  if (s.default !== undefined) return s.default;
  if (s.enum && s.enum.length > 0) return s.enum[0];
  switch (schemaType(s)) {
    case "string":
      return "";
    case "number":
    case "integer":
      return undefined;
    case "boolean":
      return false;
    case "array":
      return [];
    case "object":
      return {};
    default:
      return "";
  }
}

interface FieldProps {
  schema: JsonSchema;
  value: unknown;
  onChange: (v: unknown) => void;
  /** The field's key in its parent object — used to infer entity references. */
  name?: string;
  label?: string;
  required?: boolean;
}

function Label({ label, required, hint }: { label?: string | undefined; required?: boolean | undefined; hint?: string | undefined }): JSX.Element | null {
  if (!label) return null;
  return (
    <div className="field-label">
      <span>
        {label}
        {required ? <span className="required">*</span> : null}
      </span>
      {hint ? <span className="field-hint">{hint}</span> : null}
    </div>
  );
}

function StringField({ schema, value, onChange, label, required }: FieldProps): JSX.Element {
  const v = typeof value === "string" ? value : "";
  const multiline = (schema.maxLength ?? 0) > 160 || schema.format === "textarea";
  return (
    <div className="field">
      <Label label={label} required={required} hint={schema.description} />
      {multiline ? (
        <AutoTextarea rows={3} value={v} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className="field-input" type="text" value={v} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function NumberField({ schema, value, onChange, label, required }: FieldProps): JSX.Element {
  const v = typeof value === "number" ? String(value) : "";
  return (
    <div className="field">
      <Label label={label} required={required} hint={schema.description} />
      <input
        className="field-input"
        type="number"
        value={v}
        onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
      />
    </div>
  );
}

function BooleanField({ schema, value, onChange, label }: FieldProps): JSX.Element {
  return (
    <div className="field field-check">
      <label className="checkbox">
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        <span>{label ?? ""}</span>
      </label>
      {schema.description ? <span className="field-hint">{schema.description}</span> : null}
    </div>
  );
}

function EnumField({ schema, value, onChange, label, required }: FieldProps): JSX.Element {
  const options = schema.enum ?? [];
  return (
    <div className="field">
      <Label label={label} required={required} hint={schema.description} />
      <select className="field-input" value={String(value ?? options[0] ?? "")} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={String(o)} value={String(o)}>
            {String(o)}
          </option>
        ))}
      </select>
    </div>
  );
}

function StringArrayField({ value, onChange, label, required, schema }: FieldProps): JSX.Element {
  const items = Array.isArray(value) ? (value as string[]) : [];
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLInputElement | null>(null);
  const commit = useCallback(
    (raw: string) => {
      const t = raw.trim();
      if (!t) return;
      onChange([...items, t]);
      setDraft("");
      ref.current?.focus();
    },
    [items, onChange],
  );
  return (
    <div className="field">
      <Label label={label} required={required} hint={schema.description} />
      <ol className="taglist">
        {items.map((item, i) => (
          <li key={i} className="taglist-item">
            <span className="taglist-rank">{String(i + 1).padStart(2, "0")}</span>
            <input
              className="field-input"
              value={item}
              onChange={(e) => {
                const next = items.slice();
                next[i] = e.target.value;
                onChange(next);
              }}
            />
            <button type="button" className="taglist-x" tabIndex={-1} onClick={() => onChange(items.filter((_, j) => j !== i))}>
              ✕
            </button>
          </li>
        ))}
        <li className="taglist-item taglist-add">
          <span className="taglist-rank">{String(items.length + 1).padStart(2, "0")}</span>
          <input
            ref={ref}
            className="field-input"
            placeholder="add — Enter to commit…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit(draft);
              } else if (e.key === "Backspace" && draft === "" && items.length > 0) {
                onChange(items.slice(0, -1));
              }
            }}
            onBlur={() => draft.trim() && commit(draft)}
          />
        </li>
      </ol>
    </div>
  );
}

function ObjectField({ schema, value, onChange }: FieldProps): JSX.Element | null {
  const props = schema.properties ?? {};
  if (Object.keys(props).length === 0) return null; // free-form object — filled programmatically
  const obj = (value && typeof value === "object" ? value : {}) as Record<string, unknown>;
  const req = new Set(schema.required ?? []);
  return (
    <div className="field-group">
      {Object.entries(props).map(([key, sub]) => (
        <Field
          key={key}
          schema={sub}
          value={obj[key]}
          onChange={(v) => onChange({ ...obj, [key]: v })}
          required={req.has(key)}
          name={key}
          label={sub.title ?? key}
        />
      ))}
    </div>
  );
}

/**
 * A searchable, keyboard-driven picker over named entities. It stores the entity's
 * id but only ever DISPLAYS its name — so an id never reaches the user's eyes.
 * `multi` mode accumulates ids as removable chips (for array reference fields).
 *
 * Keyboard: type to filter · ↑↓ move · Enter/Tab select · Esc closes the dropdown
 * (and is stopped from bubbling, so it doesn't close the whole palette).
 */
function NamePicker({
  schema,
  value,
  onChange,
  label,
  required,
  options,
  multi,
}: FieldProps & { options: readonly ResolvedRef[]; multi: boolean }): JSX.Element {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [hi, setHi] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const chosen = multi ? (Array.isArray(value) ? (value as string[]) : []) : [];
  const single = !multi && typeof value === "string" ? value : "";
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = multi ? options.filter((o) => !chosen.includes(o.id)) : options;
    return q ? pool.filter((o) => `${o.label} ${o.sublabel ?? ""}`.toLowerCase().includes(q)) : pool;
  }, [query, options, multi, chosen]);

  const pick = (ref: ResolvedRef | undefined): void => {
    if (!ref) return;
    if (multi) {
      onChange([...chosen, ref.id]);
      setQuery("");
      setHi(0);
      inputRef.current?.focus();
    } else {
      onChange(ref.id);
      setQuery("");
      setOpen(false);
    }
  };

  const selectedRef = single ? byId.get(single) : undefined;
  // What the input box shows: the live query while open, else the resolved name.
  const display = open ? query : multi ? "" : selectedRef?.label ?? (single ? single : "");

  // Keep the highlighted option inside the scrolling menu — in BOTH directions.
  // Shares `keepInView` with the command list so the two navigate identically:
  // it writes only this menu's scrollTop by the exact overflow (never
  // scrollIntoView, which walks ancestor scrollers and lags). useLayoutEffect
  // (keyed on `hi`) so the highlight move and the scroll land in the same frame.
  useLayoutEffect(() => {
    if (!open) return;
    const menu = menuRef.current;
    const active = menu?.querySelector<HTMLElement>(".picker-opt.hi");
    if (menu && active) keepInView(menu, active);
  }, [hi, open, matches.length]);

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    // Shift+Arrow extends the native text selection — let it through (don't drive the
    // option list), so the typed query can be selected with the keyboard like any input.
    if (e.key === "ArrowDown" && !e.shiftKey) {
      e.preventDefault();
      setOpen(true);
      setHi((h) => Math.min(matches.length - 1, h + 1));
    } else if (e.key === "ArrowUp" && !e.shiftKey) {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      if (open && matches.length > 0) {
        e.preventDefault();
        pick(matches[Math.min(hi, matches.length - 1)]);
      }
    } else if (e.key === "Tab") {
      // Commit the highlight on the way out, so Tab both selects and advances.
      if (open && matches.length > 0 && (query.trim() || multi)) pick(matches[Math.min(hi, matches.length - 1)]);
      setOpen(false);
    } else if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        e.stopPropagation(); // do NOT let Esc bubble up and close the palette
        setOpen(false);
        setQuery("");
      }
    } else if (e.key === "Backspace" && multi && query === "" && chosen.length > 0) {
      onChange(chosen.slice(0, -1));
    }
  };

  return (
    <div className="field">
      <Label label={label} required={required} hint={schema.description} />
      {multi && chosen.length > 0 ? (
        <div className="picker-chips">
          {chosen.map((id) => (
            <span key={id} className="picker-chip">
              {byId.get(id)?.label ?? id}
              <button type="button" className="picker-chip-x" tabIndex={-1} onClick={() => onChange(chosen.filter((x) => x !== id))}>
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : null}
      <div className="picker">
        <input
          ref={inputRef}
          className="field-input"
          type="text"
          value={display}
          placeholder={options.length === 0 ? "— none available yet —" : multi ? "search to add…" : "search by name…"}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setHi(0);
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={onKey}
        />
        {!multi && selectedRef ? <span className="picker-resolved" title={single}>{selectedRef.sublabel ?? selectedRef.kind}</span> : null}
        {open && matches.length > 0 ? (
          <div className="picker-menu" ref={menuRef}>
            {/* Render every match (not a slice): `hi` can reach matches.length-1 via
                ArrowDown, so a capped list would leave the active row unrendered and
                impossible to scroll to. The menu's max-height + overflow-y scroll it. */}
            {matches.map((o, i) => (
              <button
                type="button"
                key={o.id}
                className={`picker-opt ${i === hi ? "hi" : ""}`}
                onMouseEnter={() => setHi(i)}
                onMouseDown={(e) => {
                  e.preventDefault(); // keep focus; fire before blur
                  pick(o);
                }}
              >
                <span className="picker-opt-label">{o.label}</span>
                {o.sublabel ? <span className="picker-opt-sub">{o.sublabel}</span> : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function Field(props: FieldProps): JSX.Element | null {
  const { schema, name } = props;
  const lookup = useContext(RefLookupContext);
  if (schema.enum) return <EnumField {...props} />;
  if (schema.const !== undefined) return null;
  const type = schemaType(schema);
  // A string or string-array field whose NAME references an entity becomes a
  // name-picker — but only when introspection is available and actually has options.
  if (lookup && name && (type === "string" || type === "array")) {
    const kind = refKindForField(name);
    if (kind) {
      const options = lookup(kind);
      if (options.length > 0) return <NamePicker {...props} options={options} multi={type === "array"} />;
    }
  }
  switch (type) {
    case "string":
      return <StringField {...props} />;
    case "number":
    case "integer":
      return <NumberField {...props} />;
    case "boolean":
      return <BooleanField {...props} />;
    case "array":
      return <StringArrayField {...props} />;
    case "object":
      return <ObjectField {...props} />;
    default:
      return <StringField {...props} />;
  }
}

export function SchemaForm({
  schema,
  initialValue,
  submitLabel,
  busy,
  submitDisabled,
  onSubmit,
  onChange,
  lookupRefs,
}: {
  schema: JsonSchema;
  initialValue?: unknown;
  submitLabel?: string;
  busy?: boolean;
  /** Disable the submit button even when not busy (e.g. a query result is current). */
  submitDisabled?: boolean;
  onSubmit: (value: unknown) => void | Promise<void>;
  /** Fires on every user edit — lets the host mark the form "dirty" since last run. */
  onChange?: (value: unknown) => void;
  /** Resolve the named options behind a reference field (enables name-pickers). */
  lookupRefs?: RefLookup;
}): JSX.Element {
  const [value, setValue] = useState<unknown>(() => initialValue ?? defaultFor(schema));
  const update = useCallback(
    (v: unknown) => {
      setValue(v);
      onChange?.(v);
    },
    [onChange],
  );
  return (
    <RefLookupContext.Provider value={lookupRefs ?? null}>
      <form
        className="schema-form"
        onSubmit={(e) => {
          e.preventDefault();
          // Strip empty-string / empty-array optionals so they don't fail constraints.
          const cleaned =
            value && typeof value === "object"
              ? Object.fromEntries(
                  Object.entries(value as Record<string, unknown>).filter(
                    ([, v]) => v !== "" && v !== undefined && !(Array.isArray(v) && v.length === 0),
                  ),
                )
              : value;
          void onSubmit(cleaned);
        }}
      >
        <Field schema={schema} value={value} onChange={update} />
        <div className="form-actions">
          <button type="submit" className="btn primary" disabled={busy === true || submitDisabled === true}>
            {busy === true ? "running…" : submitLabel ?? "Run"}
          </button>
        </div>
      </form>
    </RefLookupContext.Provider>
  );
}

export { defaultFor as emptyValueFor };
